import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GUILD_PLANS, type GuildPlan } from '@frankenstein/shared';
import { ApiError, api } from '../lib/api.js';
import type { MeResponse } from '../lib/queries.js';
import {
  useAdminGuilds,
  useAdminGuildMutations,
  useAllowlist,
  useAllowlistMutations,
} from '../lib/adminQueries.js';
import { LanguageSwitcher } from '../components/LanguageSwitcher.js';

export const Route = createFileRoute('/admin')({
  // SPEC §5.14 (FRK-136): master-admin only. Non-supers bounce to home.
  beforeLoad: async () => {
    let me: MeResponse;
    try {
      me = await api<MeResponse>('/api/auth/me');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) throw redirect({ to: '/login' });
      throw err;
    }
    if (!me.isSuperAdmin) throw redirect({ to: '/' });
  },
  component: AdminPage,
});

function AdminPage() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <header className="border-b border-zinc-800">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <img src="/icon.png" alt="Frankenstein" className="h-6 w-6" />
            <span className="text-sm text-zinc-400 hover:text-zinc-200">← {t('admin.back')}</span>
          </Link>
          <span className="ml-2 rounded-full border border-rose-700/60 bg-rose-950/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-rose-300">
            Super Admin
          </span>
          <div className="ml-auto">
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-10 px-6 py-8">
        <div>
          <h1 className="text-2xl font-bold">{t('admin.title')}</h1>
          <p className="mt-1 text-sm text-zinc-400">{t('admin.subtitle')}</p>
        </div>
        <AllowlistSection />
        <GuildsSection />
      </main>
    </div>
  );
}

function AllowlistSection() {
  const { t } = useTranslation();
  const list = useAllowlist();
  const m = useAllowlistMutations();
  const [userId, setUserId] = useState('');
  const [note, setNote] = useState('');
  const rows = list.data ?? [];

  const submit = (): void => {
    if (!/^\d{17,20}$/.test(userId)) return;
    const trimmed = note.trim();
    const body = trimmed ? { userId, note: trimmed } : { userId };
    m.add.mutate(body, {
      onSuccess: () => {
        setUserId('');
        setNote('');
      },
    });
  };

  return (
    <section>
      <h2 className="text-lg font-semibold">{t('admin.allowlist.title')}</h2>
      <p className="mb-3 text-sm text-zinc-400">{t('admin.allowlist.desc')}</p>

      <div className="mb-4 flex flex-wrap gap-2">
        <input
          type="text"
          value={userId}
          placeholder={t('admin.allowlist.userIdPlaceholder')}
          onChange={(e) => {
            setUserId(e.target.value);
          }}
          className="w-56 rounded-md border border-zinc-800 bg-zinc-950 px-3 py-1.5 font-mono text-sm"
        />
        <input
          type="text"
          value={note}
          placeholder={t('admin.allowlist.notePlaceholder')}
          maxLength={200}
          onChange={(e) => {
            setNote(e.target.value);
          }}
          className="flex-1 rounded-md border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-sm"
        />
        <button
          type="button"
          disabled={!/^\d{17,20}$/.test(userId) || m.add.isPending}
          onClick={submit}
          className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
        >
          {t('admin.allowlist.add')}
        </button>
      </div>

      {rows.length === 0 ? (
        <p className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-6 text-center text-sm text-zinc-500">
          {t('admin.allowlist.empty')}
        </p>
      ) : (
        <div className="overflow-hidden rounded-xl border border-zinc-800">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/60 text-left text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-4 py-2">{t('admin.allowlist.col.user')}</th>
                <th className="px-4 py-2">{t('admin.allowlist.col.note')}</th>
                <th className="px-4 py-2 text-right" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/60">
              {rows.map((u) => (
                <tr key={u.userId} className="hover:bg-zinc-900/40">
                  <td className="px-4 py-2 font-mono text-xs">{u.userId}</td>
                  <td className="px-4 py-2 text-zinc-400">{u.note ?? '—'}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      disabled={m.remove.isPending}
                      onClick={() => {
                        m.remove.mutate(u.userId);
                      }}
                      className="text-xs text-rose-400 hover:text-rose-300 disabled:opacity-50"
                    >
                      {t('admin.allowlist.remove')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function GuildsSection() {
  const { t } = useTranslation();
  const list = useAdminGuilds();
  const m = useAdminGuildMutations();
  const rows = list.data ?? [];

  return (
    <section>
      <h2 className="text-lg font-semibold">{t('admin.guilds.title')}</h2>
      <p className="mb-3 text-sm text-zinc-400">{t('admin.guilds.desc')}</p>

      {rows.length === 0 ? (
        <p className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-6 text-center text-sm text-zinc-500">
          {t('admin.guilds.empty')}
        </p>
      ) : (
        <div className="overflow-hidden rounded-xl border border-zinc-800">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/60 text-left text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-4 py-2">{t('admin.guilds.col.name')}</th>
                <th className="px-4 py-2">{t('admin.guilds.col.members')}</th>
                <th className="px-4 py-2">{t('admin.guilds.col.plan')}</th>
                <th className="px-4 py-2 text-right" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/60">
              {rows.map((g) => (
                <tr key={g.id} className="hover:bg-zinc-900/40">
                  <td className="px-4 py-2">
                    <div className="font-medium">{g.name}</div>
                    <code className="font-mono text-xs text-zinc-600">{g.id}</code>
                  </td>
                  <td className="px-4 py-2 text-zinc-400">{g.memberCount ?? '—'}</td>
                  <td className="px-4 py-2">
                    <select
                      value={g.plan}
                      disabled={m.setPlan.isPending}
                      onChange={(e) => {
                        m.setPlan.mutate({ id: g.id, plan: e.target.value as GuildPlan });
                      }}
                      className="rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1 text-sm"
                    >
                      {GUILD_PLANS.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      disabled={m.leave.isPending}
                      onClick={() => {
                        if (confirm(t('admin.guilds.leaveConfirm', { name: g.name })))
                          m.leave.mutate(g.id);
                      }}
                      className="text-xs text-rose-400 hover:text-rose-300 disabled:opacity-50"
                    >
                      {t('admin.guilds.leave')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
